<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

import type { VueClass } from '@/kasten-components/types';
import { sleep } from '@/kasten-components/utils';
import { copyToClipboard } from '@/utils/clipboard';
import { cnMerge } from '@/utils/utils.js';
import CheckIcon from '~icons/ion/checkmark';
import CopyIcon from '~icons/ion/copy';

export interface KaCopyButtonProps {
  id?: string;
  dataTestid?: string;
  label?: string;
  hideLabel?: boolean;
  command: string;
  successLabel?: string;
  errorLabel?: string;
  class?: VueClass;
}

const props = withDefaults(defineProps<KaCopyButtonProps>(), {
  label: 'Copy to clipboard',
  successLabel: 'Copied to clipboard',
  errorLabel: 'Error',
  hideLabel: false,
  class: '',
});

const emit = defineEmits<{
  'copy:success': [];
  'copy:error': [error: unknown];
}>();

const isCopying = ref(false);
const isError = ref(false);
const copied = ref(false);
const alert = ref('');

const internalLabel = computed(() => {
  if (isError.value) return props.errorLabel;
  return copied.value ? props.successLabel : props.label;
});

const handleCopy = async () => {
  if (isCopying.value) return;

  isCopying.value = true;
  isError.value = false;
  copied.value = false;
  alert.value = '';

  // comment to test ci tests
  // another comment to test ci tests
  // another another comment to test ci tests

  try {
    await copyToClipboard(props.command);
    emit('copy:success');

    copied.value = true;

    await nextTick();
    alert.value = 'Copied';

    await sleep(1500);

    copied.value = false;
    // Reset alert, we cannot set this to an empty string as screen readers will ignore it
    alert.value = 'Click to copy';
  } catch (err) {
    isError.value = true;

    emit('copy:error', err);
    // eslint-disable-next-line no-console
    console.error(err);

    await nextTick();
    alert.value = props.errorLabel;
  } finally {
    isCopying.value = false;
  }
};
</script>
<template>
  <div class="m-0 p-0">
    <button
      :id="props.id"
      :data-testid="props.dataTestid"
      :class="
        cnMerge(
          'inline-flex flex-shrink-0 items-center justify-center gap-1.5 rounded border border-gray-300 bg-white transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 disabled:pointer-events-none disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700',
          props.hideLabel ? 'p-1.5' : 'w-48 py-2 pl-4 pr-5',
          props.class,
        )
      "
      type="button"
      @click="handleCopy"
    >
      <span class="inline-flex h-5 w-5 flex-none items-center justify-center" aria-hidden="true">
        <CopyIcon v-if="!copied" class="block h-3.5 w-3.5 rotate-90 text-gray-400" />
        <CheckIcon v-else class="block size-5 font-bold text-green-600 dark:text-green-400" />
      </span>

      <span
        :class="[
          'text-sm font-normal text-gray-900 dark:text-gray-200',
          { 'sr-only': props.hideLabel },
        ]"
      >
        <slot>{{ internalLabel }}</slot>
      </span>
    </button>

    <span
      :data-testid="props.dataTestid ? `${props.dataTestid}-copied-alert` : undefined"
      class="sr-only"
      role="alert"
      aria-live="assertive"
      >{{ alert }}</span
    >
  </div>
</template>
